<template>
  <Modal
    :open="open"
    @close-modal="handleCancel"
    custom-height="h-auto"
    custom-width="lg:w-[890px] max-h-[80vh]"
  >
    <template #modal-header>
      <div class="flex flex-col font-inter">
        <div class="flex gap-2 items-center">
          <Icon name="Pencil" class="h-4 w-4 text-info" />
          <p class="text-sm text-info font-semibold font-inter">Complete your investment</p>
        </div>
        <h3 class="font-semibold text-[28px] text-strongest">
          Suitability questionnaire revision needed
        </h3>
      </div>
    </template>

    <template #modal-body>
      <div v-if="currentIssue" class="p-6 text-strongest">
        <div class="flex overflow-x-auto no-scrollbar mb-6">
          <div
            v-for="(_, i) in issues"
            :key="i"
            class="text-strongest flex items-center flex-shrink-0"
          >
            <div
              class="cursor-pointer px-3 h-10 flex items-center rounded-full text-sm whitespace-nowrap font-inter"
              :class="i === currentIndex ? 'bg-tab-bg-selected' : 'bg-transparent'"
              @click="currentIndex = i"
            >
              <div class="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <div
                  v-if="resolvedIssues[i]"
                  class="h-4 w-4 rounded-full bg-success flex items-center justify-center"
                >
                  <Icon name="Check" class="h-2.5 w-2.5 text-strongest-inverse" :stroke-width="3" />
                </div>
                <Icon
                  v-else-if="i === currentIndex"
                  name="Circle"
                  class="h-4 w-4 text-filter-outline-selected-chip-icon"
                />
                <Icon v-else name="Circle" class="h-4 w-4 text-outline-dividers" />
              </div>
              <p class="ml-2.5" :class="i === currentIndex ? 'font-semibold' : 'font-normal'">
                <span class="invisible font-inter font-semibold h-0 block">Issue {{ i + 1 }}</span>
                <span class="font-inter">Issue {{ i + 1 }}</span>
              </p>
            </div>
            <div
              v-if="i < issues.length - 1"
              class="w-[14px] h-0.5 mx-3 bg-outline-dividers flex-shrink-0"
            />
          </div>
        </div>

        <!-- Attention required -->
        <p class="font-inter font-semibold text-strongest mb-2">Attention required</p>
        <div class="bg-container-bg rounded-xl p-4 mb-6">
          <div class="flex items-center gap-2 mb-2">
            <Icon name="Asterisk" class="h-4.5 w-4.5 text-strong -skew-x-12 shrink-0" />
            <span class="font-inter font-semibold text-strong text-sm">{{
              currentIssue.attention_header
            }}</span>
          </div>
          <ul class="list-disc pl-5 space-y-1">
            <li
              v-for="msg in currentIssueDetails"
              :key="msg"
              class="font-inter text-sm font-semibold text-strongest"
            >
              {{ msg }}
            </li>
          </ul>
          <p
            v-if="currentIssue.additional_note"
            class="font-inter text-sm text-strong font-semibold mt-3"
          >
            {{ currentIssue.additional_note }}
          </p>
        </div>

        <!-- Financial issue: show income form -->
        <template v-if="currentIssue.type === 'financial'">
          <p class="font-inter font-semibold text-strongest mb-3">
            Update your financial information
          </p>
          <Form
            v-if="incomeSchema"
            :schema_array="incomeSchema"
            :state_object="dataStore.contact.suitability_info"
            :record_id="dataStore.contact?.record_id"
            variant="secondary"
            :errors="financialFieldErrors"
          />
        </template>

        <!-- Selection issue: two-column unacceptable / acceptable layout -->
        <template v-else>
          <!-- Suitability question to be resolved -->
          <p class="font-inter font-semibold text-strongest mb-2">
            Suitability question to be resolved
          </p>
          <div class="bg-container-bg rounded-xl p-4 mb-5">
            <p class="font-inter font-medium text-strongest">
              {{ currentIssue.question_number }}. {{ currentIssue.question_label }}
            </p>
          </div>

          <div class="grid grid-cols-[1fr_32px_1fr] gap-3 items-start">
            <div>
              <p class="font-inter font-semibold text-strongest mb-3">Unacceptable answers</p>
              <div class="flex flex-col gap-2">
                <div
                  v-for="opt in currentIssue.unacceptable_answers"
                  :key="opt"
                  class="rounded-xl border border-outline-dividers p-3 flex items-center gap-2 h-[44px]"
                >
                  <span class="font-inter font-medium text-strongest">{{ opt }}</span>
                  <span
                    v-if="opt === currentIssue.current_answer"
                    class="font-inter text-xs font-semibold bg-container-outline rounded-full px-2 py-0.5 text-strongest whitespace-nowrap shrink-0"
                  >
                    Previous answer
                  </span>
                </div>
              </div>
            </div>

            <div class="flex items-start justify-center pt-12">
              <Icon name="ArrowRight" class="h-5 w-5 text-strong" />
            </div>

            <div>
              <p class="font-inter font-semibold text-strongest mb-3">
                Acceptable answers (please select one)
              </p>
              <div class="flex flex-col gap-2">
                <label
                  v-for="opt in currentIssue.acceptable_answers"
                  :key="opt"
                  class="relative cursor-pointer"
                >
                  <input
                    type="radio"
                    :name="`suitability-issue-${currentIndex}`"
                    :value="opt"
                    v-model="dataStore.contact.suitability_info[currentIssue.field_name]"
                    class="sr-only"
                  />
                  <div
                    class="flex items-center gap-3 py-3 px-3 rounded-xl border bg-container-bg border-none h-[44px]"
                  >
                    <div
                      class="min-w-4 min-h-4 h-4 w-4 rounded-full border border-radio-checkbox-outline flex items-center justify-center shrink-0"
                      :class="
                        dataStore.contact.suitability_info[currentIssue.field_name] === opt
                          ? '!border-4 !border-radio-checkbox-checked'
                          : ''
                      "
                    />
                    <span class="font-inter text-sm font-medium md:text-base">{{ opt }}</span>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <!-- Occupation / Employer fields when employment_status resolves to Employed or Self-Employed -->
          <div
            v-if="
              currentIssue.field_name === 'employment_status' &&
              (dataStore.contact.suitability_info[currentIssue.field_name] === 'Employed' ||
                dataStore.contact.suitability_info[currentIssue.field_name] === 'Self-Employed')
            "
            class="mt-4 flex flex-col md:flex-row gap-3"
          >
            <div class="flex-1">
              <BaseInput
                label="Occupation"
                field_name="occupation"
                field_type="string"
                v-model="dataStore.contact.occupation"
                :error_message="!dataStore.contact.occupation?.trim() ? 'This field is required.' : ''"
              />
            </div>
            <div class="flex-1">
              <BaseInput
                label="Employer"
                field_name="employer"
                field_type="string"
                v-model="dataStore.contact.employer"
                :error_message="!dataStore.contact.employer?.trim() ? 'This field is required.' : ''"
              />
            </div>
          </div>
        </template>
      </div>
    </template>

    <template #modal-footer>
      <div class="w-full flex items-center justify-end font-inter">
        <div class="flex gap-2">
          <Button @click="handleCancel" variant="paper-primary" size="md">
            <template #label>Cancel</template>
          </Button>
          <Button
            @click="handleSaveAndContinue"
            variant="primary-grey"
            size="md"
            :disabled="!currentAnswerIsAcceptable"
          >
            <template #label>{{ allIssuesResolved ? "Save" : "Save and continue" }}</template>
          </Button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { Modal, Button, Icon, Form, BaseInput } from "@/components";
import { useDataStore, usePurchaseFlowStore } from "@/stores";

const props = defineProps({
  open: { type: Boolean, required: true },
  issues: { type: Array, required: true },
});

const emit = defineEmits(["close", "save"]);

const dataStore = useDataStore();
const purchaseFlowStore = usePurchaseFlowStore();
const currentIndex = ref(0);
const snapshot = ref(null);

const incomeSchema = computed(
  () =>
    purchaseFlowStore.suitability_questions.find((s) => s.group_name === "Income Information")
      ?.fields
);

const isShortTerm = computed(() => purchaseFlowStore.current_investment?.is_short_term === true);

const investmentAmount = computed(() => {
  const amt = purchaseFlowStore.current_investment?.amount;
  return typeof amt === "string" ? Number(amt.replace(/,/g, "")) : Number(amt) || 0;
});

const isShortTermUnderThreshold = computed(() => isShortTerm.value && investmentAmount.value < 10000);

// S1 financial check applies to all regular S1 investments, and to short-term S1 only when amount >= $10k
const s1FinancialCheckApplies = computed(() => {
  if (purchaseFlowStore.selected_offering?.type !== "S1") return false;
  if (isShortTermUnderThreshold.value) return false;
  return true;
});

// Short-term S1 uses 45/45/150 thresholds; regular S1 uses 70/70/250
const s1Thresholds = computed(() =>
  isShortTerm.value
    ? { income: 45000, netWorth: 45000, netWorthSolo: 150000 }
    : { income: 70000, netWorth: 70000, netWorthSolo: 250000 }
);

const financialFieldErrors = computed(() => {
  if (isShortTermUnderThreshold.value) return {};
  const info = dataStore.contact.suitability_info || {};
  const income = Number(String(info.current_income || "").replace(/,/g, "")) || 0;
  const netWorth = Number(String(info.current_net_worth || "").replace(/,/g, "")) || 0;

  const errs = {};
  const fmt = (n) => `$${n.toLocaleString()}`;

  if (s1FinancialCheckApplies.value && !((income >= s1Thresholds.value.income && netWorth >= s1Thresholds.value.netWorth) || netWorth >= s1Thresholds.value.netWorthSolo)) {
    if (!errs.current_income) {
      errs.current_income = `A minimum income of ${fmt(s1Thresholds.value.income)} is required for this offering, unless your net worth meets the ${fmt(s1Thresholds.value.netWorthSolo)} threshold.`;
    }
    if (!errs.current_net_worth) {
      errs.current_net_worth = `Qualification requires a minimum net worth of ${fmt(s1Thresholds.value.netWorthSolo)}, or ${fmt(s1Thresholds.value.netWorth)} for those with an annual income over ${fmt(s1Thresholds.value.income)}.`;
    }
  }

  return errs;
});

watch(
  () => props.open,
  (val) => {
    if (val) {
      currentIndex.value = 0;
      snapshot.value = { ...(dataStore.contact.suitability_info || {}) };
    }
  }
);

const currentIssue = computed(() => props.issues[currentIndex.value] ?? null);

const currentIssueDetails = computed(() => {
  if (!currentIssue.value) return [];
  return (
    currentIssue.value.details ?? (currentIssue.value.detail ? [currentIssue.value.detail] : [])
  );
});

function isIssueResolved(issue) {
  if (!issue) return false;
  if (issue.type === "financial") {
    if (isShortTermUnderThreshold.value) return true;
    const suitabilityInfo = dataStore.contact.suitability_info || {};
    const income = Number(String(suitabilityInfo.current_income || "").replace(/,/g, "")) || 0;
    const netWorth = Number(String(suitabilityInfo.current_net_worth || "").replace(/,/g, "")) || 0;
    if (s1FinancialCheckApplies.value) {
      const { income: minIncome, netWorth: minNetWorth, netWorthSolo } = s1Thresholds.value;
      if (!((income >= minIncome && netWorth >= minNetWorth) || netWorth >= netWorthSolo)) return false;
    }
    return true;
  }
  const answer = dataStore.contact.suitability_info?.[issue.field_name];
  if (!issue.acceptable_answers.includes(answer)) return false;

  if (
    issue.field_name === "employment_status" &&
    (answer === "Employed" || answer === "Self-Employed")
  ) {
    return !!(dataStore.contact.occupation?.trim() && dataStore.contact.employer?.trim());
  }

  return true;
}

const resolvedIssues = computed(() => props.issues.map((issue) => isIssueResolved(issue)));
const currentAnswerIsAcceptable = computed(() => resolvedIssues.value[currentIndex.value] ?? false);
const allIssuesResolved = computed(() => resolvedIssues.value.every(Boolean));

function handleCancel() {
  if (snapshot.value && dataStore.contact.suitability_info) {
    Object.assign(dataStore.contact.suitability_info, snapshot.value);
  }
  emit("close");
}

function handleSaveAndContinue() {
  if (!currentAnswerIsAcceptable.value) return;
  if (allIssuesResolved.value) {
    emit("save");
    return;
  }
  // Jump to next unresolved issue, wrapping around if needed
  const after = resolvedIssues.value.findIndex((r, i) => i > currentIndex.value && !r);
  if (after !== -1) {
    currentIndex.value = after;
  } else {
    const before = resolvedIssues.value.findIndex((r) => !r);
    if (before !== -1) currentIndex.value = before;
  }
}
</script>
